/**
 * EndTripModal — iter193.
 *
 * Admin clicks "End trip" → modal opens with a recap preview:
 *   • Total spent
 *   • Member count
 *   • Open-balances banner (when any member has non-zero balance)
 *   • What happens when you end (frozen for expenses, settlements stay open,
 *     30-day soft-delete window, PDF + WhatsApp on confirm)
 *   • Single Confirm button — no dual confirmation (reversible for 30 days)
 *
 * Calls:
 *   GET  /api/groups/{id}/end-trip/preview  — fetches the recap snapshot
 *   POST /api/groups/{id}/end-trip          — performs the archive + dispatch
 */
import { useEffect, useState } from 'react';
import { AlertTriangle, Flag, FileText, MessageCircle, Clock } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import { formatNXC } from '../lib/formatNXC';

export default function EndTripModal({ open, onOpenChange, group, onEnded }) {
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const groupId = group?.id;
  const currency = group?.currency || 'INR';

  useEffect(() => {
    if (!open || !groupId) return;
    let cancelled = false;
    setLoading(true);
    setPreview(null);
    api.get(`/groups/${groupId}/end-trip/preview`)
      .then((res) => {
        if (cancelled) return;
        setPreview(res.data);
      })
      .catch((e) => {
        if (cancelled) return;
        toast.error(e?.response?.data?.detail || 'Could not load trip preview');
        onOpenChange(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, groupId, onOpenChange]);

  const handleConfirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/groups/${groupId}/end-trip`);
      toast.success(res.data?.message || 'Trip ended');
      onEnded?.(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not end trip');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="end-trip-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Flag className="w-5 h-5 text-rose-600" />
            End this trip?
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="py-10 text-center text-sm text-muted-foreground" data-testid="end-trip-loading">
            Loading recap…
          </div>
        )}

        {!loading && preview && (
          <div className="space-y-3 mt-2">
            {/* Recap snapshot */}
            <div className="rounded-2xl border-2 border-border bg-muted/30 px-4 py-3 grid grid-cols-2 gap-3" data-testid="end-trip-recap">
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Total spent</p>
                <p className="font-display text-xl font-bold tabular-nums tracking-tight">{formatNXC(preview.total_spent, currency)}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Members</p>
                <p className="font-display text-xl font-bold tabular-nums tracking-tight">{preview.member_count}</p>
              </div>
            </div>

            {/* Open-balances warning */}
            {preview.has_open_balances && (
              <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-4 py-3" data-testid="end-trip-open-balances">
                <div className="flex items-start gap-2.5">
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-[12px] leading-relaxed text-amber-900 dark:text-amber-100">
                    <p className="font-bold">This trip has open balances.</p>
                    <p className="mt-0.5 text-amber-800/90 dark:text-amber-200/90">
                      Members can still settle (Pay / Receive) after the trip ends.
                      You can reopen the trip within 30 days if disputes come up.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* What happens next */}
            <div className="space-y-2 px-1">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold">What happens when you end</p>
              <ul className="space-y-1.5">
                <li className="flex items-start gap-2 text-[12px]" data-testid="end-trip-effect-frozen">
                  <Flag className="w-3.5 h-3.5 text-rose-500 mt-0.5 shrink-0" />
                  <span>Frozen for new expenses + top-ups. Settlements (Pay / Receive) stay open.</span>
                </li>
                <li className="flex items-start gap-2 text-[12px]">
                  <FileText className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                  <span>Final settlement PDF generated &amp; downloadable from the trip banner.</span>
                </li>
                <li className="flex items-start gap-2 text-[12px]">
                  <MessageCircle className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
                  <span>WhatsApp recap sent to all members (if WhatsApp is set up).</span>
                </li>
                <li className="flex items-start gap-2 text-[12px]" data-testid="end-trip-effect-retention">
                  <Clock className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
                  <span>Trip auto-deletes in <span className="font-bold">30 days</span>. Reopen anytime before then.</span>
                </li>
              </ul>
            </div>
          </div>
        )}

        <div className="flex gap-2 mt-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            className="flex-1 h-11 rounded-xl"
            data-testid="end-trip-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={submitting || loading || !preview}
            className="flex-1 h-11 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-bold"
            data-testid="end-trip-confirm-btn"
          >
            <Flag className="w-4 h-4 mr-1.5" />
            {submitting ? 'Ending…' : 'End trip'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
